import { useMemo } from 'react'
import { IMessage } from '@weblite/ts-action/message'
import { SelectableAvatar, UserAvatar } from '$common/avatar'
import { useRoom } from '$logic/room/react-hook'
import {
  getUserDisplayName,
  getUserFirstName,
  useUser,
  useUserStatus,
} from '$logic/users'
import { usePv } from '$logic/pv/react-hook'
import { getUserImageUrl } from '$func/imageUrl'
import { useRoomLastMessage } from '$logic/room-last-message/react-hook'
import { stringToColor } from '$helper/functions/string-to-color'
import { OpenStorySource } from '$logic/story/analytics'
import { AvatarSize } from '$common/avatar/avatar-base/types'
import { getAvatarText } from '$func/avatar-text'
import { ChatListItemBase } from './chat-list-item-base'
import { ChatListItemSkeleton } from './chat-list-item-skeleton'
import { EventTitle } from './components/event-title'
import { ChatListItemProps } from './chat-list-item.types'
import {
  getLastMessageText,
  getLastMessageStatus,
} from './hooks/chat-list-item-hooks'
import { usePvMessageAction } from './hooks/pv-chat-list-item-hooks'

// TODO: IMPLEMENT PENDING MESSAGE ICON IN STATUS
export function PvChatListItem({
  selected,
  pinned,
  isOnTop,
  chatId,
  onClick,
  onContextMenu,
  onLongPress,
  isOpened,
}: ChatListItemProps) {
  const { entity: pv } = usePv(chatId)
  const { to: userId, seen: pvSeen, muted, unreadMark } = pv || {}
  const { user } = useUser(userId)

  const status = useUserStatus(userId)
  const messageAction = usePvMessageAction(chatId)

  const room = useRoom(chatId)
  const lastMessage = useRoomLastMessage(chatId)
  const lastMessageSentWappName = getLastMessageText(lastMessage)
  const avatarBackgroundColor = useMemo(
    () => stringToColor(getUserFirstName(user), user?.id),
    [user],
  )
  if (!user) return <ChatListItemSkeleton />

  const { unseen, seen, received, fromMe } = getLastMessageStatus(
    lastMessage,
    pvSeen,
    room,
  )

  const title = getUserDisplayName(user)
  return (
    <ChatListItemBase
      chatId={chatId}
      selected={selected}
      title={title}
      date={lastMessage?.date}
      lastMessageType={lastMessage?.type}
      lastMessageBody={lastMessageSentWappName || ''}
      unseen={unseen}
      seen={Boolean(seen)}
      received={Boolean(received)}
      muted={muted}
      fromMe={fromMe}
      pinned={pinned}
      verified={user.verified}
      eventTitle={
        lastMessage?.type === 'EVENT' ? (
          <EventTitle lastMessage={lastMessage as IMessage} />
        ) : (
          undefined
        )
      }
      messageAction={messageAction}
      avatarElement={
        <UserAvatar
          source={OpenStorySource.ROOM_LIST}
          userId={user.id}
          checked={selected}
          size={AvatarSize.LG}
          withStory
          hasDeleted={user.deletedAt}
          online={status === 'ONLINE'}
        />
      }
      hasDeleted={user.deletedAt}
      hasMarkAsUnread={Boolean(unreadMark)}
      isOnTop={isOnTop}
      links={(lastMessage as IMessage)?.links}
      onClick={onClick}
      onContextMenu={onContextMenu}
      onLongPress={onLongPress}
      isOpened={Boolean(isOpened)}
    />
  )
}
